"use client";

import { useMemo, useState, useRef, useEffect } from "react";
import { Megaphone, ChevronRight, ChevronDown } from "lucide-react";
import { useTranslations } from "next-intl";
import { format } from "date-fns";
import { formatAppDateTime } from "@/lib/utils/format-datetime";
import { parseDateOnly } from "@/lib/reservation-utils";
import type { PartnerNotice, ProductNotice, NoticeType } from "@/types";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { cn } from "@/lib/utils";

interface ProductNoticesProps {
  partnerNotices?: PartnerNotice[];
  productNotices?: ProductNotice[];
  partnerName?: string;
  partnerIcon?: string;
}

export default function ProductNotices({
  partnerNotices,
  productNotices,
  partnerName,
  partnerIcon,
}: ProductNoticesProps) {
  const t = useTranslations("product");
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [expandedIds, setExpandedIds] = useState<Set<string>>(new Set());
  // Avoid hydration mismatch: SSR's `new Date()` and the client's hydrate-time
  // `new Date()` can fall on different sides of a date boundary (or a notice's
  // start/end date), which silently changes the filtered-and-sorted notice
  // list and trips React #418. Defer the date-based filter to the client by
  // seeding `today` only after mount; the first paint shows no notices and
  // is replaced on the next render.
  const [today, setToday] = useState<Date | null>(null);
  useEffect(() => {
    setToday(new Date());
  }, []);

  // Toggle expand/collapse for a notice
  const toggleExpand = (id: string) => {
    setExpandedIds((prev) => {
      const newSet = new Set(prev);
      if (newSet.has(id)) {
        newSet.delete(id);
      } else {
        newSet.add(id);
      }
      return newSet;
    });
  };

  // Get badge info based on notice type
  const getTypeBadge = (type: NoticeType) => {
    switch (type) {
      case "urgent":
        return {
          label: t("notices.urgent"),
          className: "bg-red-100 text-red-600",
        };
      case "important":
        return {
          label: t("notices.important"),
          className: "bg-pink-100 text-pink-600",
        };
      default:
        return null;
    }
  };

  // Combine and sort notices
  const allNotices = useMemo(() => {
    if (!today) return [];
    const typeOrder: Record<NoticeType, number> = {
      urgent: 0,
      important: 1,
      info: 2,
    };

    const combined: Array<{
      notice: PartnerNotice | ProductNotice;
      isPartnerNotice: boolean;
    }> = [
      ...(partnerNotices || []).map((n) => ({
        notice: n,
        isPartnerNotice: true,
      })),
      ...(productNotices || []).map((n) => ({
        notice: n,
        isPartnerNotice: false,
      })),
    ];

    // Filter by date range
    const filtered = combined.filter(({ notice }) => {
      const startDateValid =
        !notice.start_date || parseDateOnly(notice.start_date) <= today;
      const endDateValid =
        !notice.end_date || parseDateOnly(notice.end_date) >= today;
      return startDateValid && endDateValid;
    });

    return filtered.sort((a, b) => {
      if (a.isPartnerNotice !== b.isPartnerNotice) {
        return a.isPartnerNotice ? -1 : 1;
      }
      const typeA = typeOrder[a.notice.notice_type];
      const typeB = typeOrder[b.notice.notice_type];
      if (typeA !== typeB) return typeA - typeB;
      if (a.notice.is_pinned !== b.notice.is_pinned) {
        return a.notice.is_pinned ? -1 : 1;
      }
      return (
        new Date(b.notice.created_at).getTime() -
        new Date(a.notice.created_at).getTime()
      );
    });
  }, [partnerNotices, productNotices, today]);

  if (allNotices.length === 0) {
    return null;
  }

  // Notice card component for the full view
  const NoticeCard = ({
    notice,
    isPartnerNotice,
  }: {
    notice: PartnerNotice | ProductNotice;
    isPartnerNotice: boolean;
  }) => {
    const isExpanded = expandedIds.has(notice.id);
    const badge = getTypeBadge(notice.notice_type);
    const contentRef = useRef<HTMLParagraphElement>(null);
    const [isTruncated, setIsTruncated] = useState(false);

    // Check if content is truncated (needs "show more" button)
    useEffect(() => {
      const el = contentRef.current;
      if (el) {
        // Check if scrollHeight > clientHeight (content overflows)
        setIsTruncated(el.scrollHeight > el.clientHeight);
      }
    }, [notice.content]);

    return (
      <div className="p-4 bg-white rounded-2xl shadow-sm border border-gray-100">
        {/* Header: Partner info + Badge */}
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-2">
            {/* Partner icon */}
            {partnerIcon ? (
              <img
                src={partnerIcon}
                alt={partnerName || ""}
                className="w-6 h-6 rounded-full object-cover"
              />
            ) : (
              <div className="w-6 h-6 rounded-full bg-orange-100 flex items-center justify-center">
                <Megaphone className="h-4 w-4 text-orange-600" />
              </div>
            )}
            <span className="font-medium text-gray-900 text-sm">
              {isPartnerNotice && partnerName
                ? partnerName
                : t("notices.productNotice")}
            </span>
          </div>
          {/* Type badge */}
          {badge && (
            <span
              className={cn(
                "px-2 py-0.5 text-xs font-medium rounded",
                badge.className,
              )}
            >
              {badge.label}
            </span>
          )}
        </div>

        {/* Content */}
        <p
          ref={contentRef}
          className={cn(
            "text-sm text-gray-700 whitespace-pre-wrap leading-relaxed wrap-break-word",
            !isExpanded && "line-clamp-4",
          )}
        >
          {notice.content}
        </p>

        {/* Show more button - only show if content is truncated */}
        {(isTruncated || isExpanded) && (
          <button
            onClick={() => toggleExpand(notice.id)}
            className="flex items-center gap-1 text-sm text-orange-500 mt-2 hover:text-orange-600"
          >
            {isExpanded ? t("notices.collapse") : t("notices.expand")}
            <ChevronDown
              className={cn(
                "h-4 w-4 transition-transform",
                isExpanded && "rotate-180",
              )}
            />
          </button>
        )}

        {/* Date */}
        <p className="text-sm text-gray-400 mt-3">
          {formatAppDateTime(notice.created_at, "yyyy.MM.dd")}
        </p>
      </div>
    );
  };

  // Full notices list for dialog/drawer
  const noticesList = (
    <div className="space-y-3">
      {allNotices.map(({ notice, isPartnerNotice }) => (
        <NoticeCard
          key={notice.id}
          notice={notice}
          isPartnerNotice={isPartnerNotice}
        />
      ))}
    </div>
  );

  return (
    <div className="py-6 border-b">
      {/* Header */}
      <div className="flex items-center justify-between mb-4">
        <h2 className="text-lg sm:text-xl font-semibold">
          {t("notices.title")}
        </h2>
        <button
          onClick={() => setIsDialogOpen(true)}
          className="flex items-center gap-1 text-sm text-gray-500 hover:text-gray-700"
        >
          {t("notices.viewAll")}
          <ChevronRight className="h-4 w-4" />
        </button>
      </div>

      {/* Notice card(s): full-width if single, swipeable if multiple */}
      {allNotices.length === 1 ? (
        (() => {
          const { notice } = allNotices[0];
          const badge = getTypeBadge(notice.notice_type);
          return (
            <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl">
              <div className="w-10 h-10 bg-white border border-amber-400 rounded-lg flex items-center justify-center shrink-0">
                <Megaphone className="h-5 w-5 text-amber-600" />
              </div>
              <div className="flex-1 min-w-0">
                {badge && (
                  <span
                    className={cn(
                      "inline-block px-1.5 py-0.5 text-xs font-medium rounded mb-1",
                      badge.className,
                    )}
                  >
                    {badge.label}
                  </span>
                )}
                <p className="text-sm text-amber-900 line-clamp-3 wrap-break-word">
                  {notice.content}
                </p>
              </div>
            </div>
          );
        })()
      ) : (
        <div className="relative -mx-4 px-4">
          <div className="flex gap-3 overflow-x-auto scrollbar-hide pb-2 snap-x snap-mandatory">
            {allNotices.map(({ notice }) => {
              const badge = getTypeBadge(notice.notice_type);
              return (
                <div key={notice.id} className="shrink-0 w-[90%] snap-start">
                  <div className="flex items-start gap-3 p-4 bg-amber-50 border border-amber-200 rounded-xl h-full">
                    <div className="w-10 h-10 bg-white border border-amber-400 rounded-lg flex items-center justify-center shrink-0">
                      <Megaphone className="h-5 w-5 text-amber-600" />
                    </div>
                    <div className="flex-1 min-w-0">
                      {badge && (
                        <span
                          className={cn(
                            "inline-block px-1.5 py-0.5 text-xs font-medium rounded mb-1",
                            badge.className,
                          )}
                        >
                          {badge.label}
                        </span>
                      )}
                      <p className="text-sm text-amber-900 line-clamp-3 wrap-break-word">
                        {notice.content}
                      </p>
                    </div>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      )}

      {/* Full notices modal */}
      <ResponsiveModal
        open={isDialogOpen}
        onOpenChange={setIsDialogOpen}
        title={t("notices.title")}
        drawerHeight="tall"
        className="max-w-lg"
      >
        {noticesList}
      </ResponsiveModal>
    </div>
  );
}
